import React from 'react'
import { X } from 'lucide-react'

interface RecordValueModalProps {
  isOpen: boolean
  title: string
  onSave: (value: number) => void
  onCancel: () => void
}

const RecordValueModal: React.FC<RecordValueModalProps> = ({
  isOpen,
  title,
  onSave,
  onCancel,
}) => {
  const [inputValue, setInputValue] = React.useState<string>('')

  const handleNumberClick = (num: string) => {
    setInputValue((prev) => prev + num)
  }

  const handleDecimal = () => {
    // Only add decimal if it doesn't already exist
    if (!inputValue.includes('.')) {
      setInputValue((prev) => (prev === '' ? '0.' : prev + '.'))
    }
  }

  const handleBackspace = () => {
    setInputValue((prev) => prev.slice(0, -1))
  }

  const handleSave = () => {
    const numValue = parseFloat(inputValue)
    if (!isNaN(numValue) && inputValue.trim() !== '') {
      onSave(numValue)
      setInputValue('')
    }
  }

  const handleCancel = () => {
    setInputValue('')
    onCancel()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-sm mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
          <button
            onClick={handleCancel}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Input Display */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <p className="text-xs text-gray-600 mb-2">Value</p>
            <input
              type="text"
              value={inputValue}
              readOnly
              className="w-full text-right text-3xl font-bold text-gray-900 bg-transparent outline-none"
              placeholder="0"
            />
          </div>

          {/* Numeric Keypad */}
          <div className="grid grid-cols-3 gap-2 mb-4">
            {/* Numbers 1-9 */}
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                key={num}
                onClick={() => handleNumberClick(num.toString())}
                className="bg-green-50 hover:bg-green-100 text-gray-900 font-semibold py-3 rounded-lg transition-colors active:bg-green-200"
              >
                {num}
              </button>
            ))}

            {/* Decimal Point */}
            <button
              onClick={handleDecimal}
              className="bg-green-50 hover:bg-green-100 text-gray-900 font-semibold py-3 rounded-lg transition-colors active:bg-green-200 col-span-1"
            >
              .
            </button>

            {/* Zero */}
            <button
              onClick={() => handleNumberClick('0')}
              className="bg-green-50 hover:bg-green-100 text-gray-900 font-semibold py-3 rounded-lg transition-colors active:bg-green-200"
            >
              0
            </button>

            {/* Backspace */}
            <button
              onClick={handleBackspace}
              className="bg-red-50 hover:bg-red-100 text-red-700 font-semibold py-3 rounded-lg transition-colors active:bg-red-200 col-span-1"
            >
              ⌫
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleCancel}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={inputValue.trim() === '' || inputValue === '.'}
              className="flex-1 px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RecordValueModal
